import styled, { css } from 'styled-components';
import { BUTTON_TYPES } from '_constants';
import { UiColors } from 'assets/styles/old/global';
import { primaryButtonSurface } from '../buttonSurface';
import { Image } from '../Img/styles';
// import { Wrapper as LoaderWrapper, Loader as LoaderImg } from '../Loader/styles';

export const ButtonTitle = styled.span`
  display: block;
  flex-shrink: 1;
  width: auto;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  /* New design: button labels are never truncated, so the legacy shrink-and-ellipsize is off.
     ButtonWrapper's min-width (below) is what actually guarantees the room. */
  html[data-new-ui='true'] & {
    flex-shrink: 0;
    overflow: visible;
    text-overflow: clip;
  }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  opacity: 0.8;
  animation: button-spinner-spin 0.8s linear infinite;
  flex-shrink: 0;

  @keyframes button-spinner-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* eslint-disable */
export const ButtonWrapper = styled.button`
  height: 40px;
  /* Geometry comes from the new-design tokens in assets/styles/base.scss, which default to these
     legacy values with the flag off. The modern variants below still hardcode their own radius /
     padding, so they are unaffected either way. */
  padding: 0 var(--btn-padding-x);
  border: none;
  border-radius: var(--btn-radius);
  font-weight: var(--btn-weight);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  position: relative;
  transition: color 0.2s;
  overflow: hidden;

  ${({ variant }) => {
    switch (variant) {
      case BUTTON_TYPES.PRIMARY:
        return primaryButtonSurface;

      case BUTTON_TYPES.SECONDARY:
        return css`
          background-color: ${UiColors.forestColor};
          color: ${UiColors.peachColor};
          border-radius: 8px;
          padding: 0 16px;
          font-weight: 600;

          :hover {
            filter: brightness(1.15);
          }
          &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;

      case BUTTON_TYPES.DESTRUCTIVE:
        return css`
          background-color: ${UiColors.errorColor};
          color: ${UiColors.fillWhiteColor};
          border-radius: 8px;
          padding: 0 16px;
          font-weight: 600;

          :hover {
            filter: brightness(1.08);
          }
          &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;

      case BUTTON_TYPES.OUTLINE:
        return css`
          background-color: ${UiColors.fillWhiteColor};
          color: ${UiColors.forestColor};
          border: 1px solid ${UiColors.strokeColor};
          border-radius: 8px;
          padding: 0 14px;
          font-weight: 600;

          :hover {
            background-color: ${UiColors.cloudColor};
          }
          &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;

      case BUTTON_TYPES.DARK:
        return css`
          background-color: ${UiColors.forestColor};
          color: ${UiColors.peachColor};

          :hover {
            filter: saturate(3);
          }
          &:focus {
            border: 3px solid var(--dodger-blue);
            outline-color: var(--dodger-blue);
          }

          /* New design: DARK already carries SECONDARY's colours, so it only needs the modern
             focus ring. The hover stays the saturate() above -- a background swap here read as the
             button turning white. */
          html[data-new-ui='true'] &:focus {
            border: none;
          }
          html[data-new-ui='true'] &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;
      case BUTTON_TYPES.DANGER:
        return css`
          background-color: ${UiColors.mangoColor};
          color: ${UiColors.forestColor};

          :hover {
            filter: saturate(2);
          }

          /* New design: DANGER already carries PRIMARY's colours, so only the focus ring is new --
             the hover stays the saturate() above. */
          html[data-new-ui='true'] &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;

      case BUTTON_TYPES.GREY:
        return css`
          background-color: var(--white-smoke);
          color: ${UiColors.forestColor};

          :hover {
            background-color: var(--dark);
            color: var(--white);
          }

          /* New design: a grey block reads as disabled next to PRIMARY/SECONDARY, so GREY takes
             OUTLINE's treatment — white fill with a stroke. */
          html[data-new-ui='true'] & {
            background-color: ${UiColors.fillWhiteColor};
            border: 1px solid ${UiColors.strokeColor};
          }
          html[data-new-ui='true'] &:hover {
            background-color: ${UiColors.cloudColor};
            color: ${UiColors.forestColor};
          }
          html[data-new-ui='true'] &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;

      default:
        return css`
          background: transparent;
          color: ${UiColors.forestColor};

          :hover {
            background-color: var(--cod-grey);
          }

          /* Ghost buttons keep their legacy hover too -- the cloud tint that was here is
             near-white, which is exactly the effect being reported. */
          html[data-new-ui='true'] &:focus-visible {
            outline: 2px solid var(--dodger-blue);
            outline-offset: 2px;
          }
        `;
    }
  }}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
    `}

  /* New design: a button must never truncate its label. min-width clamps the used width up to
     the content size, and because min-width and width are separate properties the used value is
     max(min-width, width) — so this wins even over an inline style width, which is what rescues
     call sites carrying a hardcoded pixel width. flex-shrink: 0 stops a tight toolbar squeezing
     the button instead; containers here already use flex-wrap, so it wraps rather than clipping. */
  html[data-new-ui='true'] & {
    min-width: max-content;
    flex-shrink: 0;
  }

  ${Image} {
    margin-right: 5px;
  }

  & > svg {
    margin-right: 5px;
    flex: 0 0 auto;
  }
`;
/* eslint-enable */
